<template>
  <div class="tender-representative">
    <div class="tender-representative-content">
      <div class="contractor-info-head clearfix ">
        <div class="top-title">
          {{ $t('tender.representatives') }}
        </div>
        <div
          v-if="isPicView"
          class="contractor-info-buttons"
        >
          <button
            v-if="!editable"
            type="button"
            class="btn btn--darkblue"
            :disabled="!canEditRepresentative"
            @click="() => enableEdit()"
          >
            {{ $t('action.edit') }}
          </button>
          <button
            v-if="editable"
            v-hkldreset
            type="button"
            class="btn btn--darkblue"
            @click="() => cancel()"
          >
            {{ $t('action.cancel') }}
          </button>
          <button
            v-if="editable"
            v-hkldreset
            type="button"
            class="btn btn--orange"
            @click="() => save()"
          >
            {{ $t('action.save') }}
          </button>
        </div>
      </div>

      <div
        v-if="editable"
        class="editabel input-areas"
      >
        <div
          v-for="(item, index) in accounts"
          :key="index"
          class="content"
        >
          <div class="name">
            <InputField
              v-for="fullnameObj of getFullnameSorts"
              :key="fullnameObj.key"
              :label="!index && fullnameObj.isFirst ? $t('name') : ''"
              :placeholder="$t(`contact.basic.${fullnameObj.label}`)"
              :value="item.tenderAccount[`contact${fullnameObj.key}`]"
              :required="true"
              @updateValue="val => updateContactAttr(`contact${fullnameObj.key}`, val, index)"
            />
          </div>
          <InputField
            class="email"
            :class="emailErrClass(index)"
            :label="!index ? $t('user.email') : ''"
            placeholder=""
            :showColon="false"
            :value="item.username"
            :inputTip="$t('hint.inputTip.email')"
            :pattern="regex.email"
            required
            @updateValue="val => updateContactUsername(val, index)"
          />
          <InputField
            class="phone"
            :class="phoneErrClass(index)"
            :label="!index ? $t('contact.phone.primaryPhone') : ''"
            placeholder=""
            :showColon="false"
            :value="item.tenderAccount.phone"
            required
            @updateValue="val => updateContactAttr('phone', val, index)"
          />
          <InputField
            class="alt-phone"
            :class="phoneErrClass(index)"
            :label="!index ? $t('user.altPhone') : ''"
            placeholder=""
            :showColon="false"
            :showFlag="false"
            :value="item.tenderAccount.altPhone"
            @updateValue="val => updateContactAttr('altPhone', val, index)"
          />
          <i
            class="fa fa-times"
            @click="preDeleteAccount(index)"
          />
        </div>
        <span
          v-if="showAddBtn"
          class="addAccount"
          @click="addAccount"
        >{{ $t('action.addRepresentative') }}+</span>
      </div>
      <ul
        v-else
        :class="['display-table', {'creator': showInviteBtn}]"
      >
        <div class="table-header">
          <span class="name">{{ $t('name') }}</span>
          <span class="email">{{ $t('user.email') }}</span>
          <span class="phone">{{ $t('contact.phone.primaryPhone') }}</span>
          <span class="other-phone">{{ $t('user.altPhone') }}</span>
          <span class="lastlogin">{{ $t('lastLogin') }}</span>
          <span class="invited-at">{{ $t('invitedAt') }}</span>
          <span
            v-show="showInviteBtn"
            class="invite"
          />
        </div>
        <li
          v-for="(item, index) in accounts"
          :key="index"
          class="table-content"
        >
          <span class="name">{{ displayFullName(item.tenderAccount.contactFirstName, item.tenderAccount.contactLastName) }}</span>
          <span class="email">{{ item.username }}</span>
          <span class="phone">{{ item.tenderAccount.phone || $t('emptyValue') }}</span>
          <span class="phone">{{ item.tenderAccount.altPhone || $t('emptyValue') }}</span>
          <span class="lastlogin">{{ formatDate(item.lastLoginAt) }}</span>
          <span class="invited-at">{{ formatDate(item.tenderAccount.invitedAt) || '-' }}</span>
          <span
            v-show="showInviteBtn"
            class="invite"
            :style="{ visibility: isCurrentUser(item) ? 'hidden' : 'visible' }"
          >
            <div
              class="invite-btn btn btn--orange"
              @click="invite(item)"
            >{{ 'invite' }}</div>
          </span>
        </li>
      </ul>
      <Modal
        ref="confirm-delete-representative"
        :showCloseBtn="false"
        :modalId="`duplicate-representative-${+new Date()}`"
      >
        <div class="font-bold">
          {{ $t('hint.deleteTenderRepresentative') }}
        </div>
        <div slot="btns">
          <button
            class="btn btn--darkblue"
            @click="closeDeleteConfirmModal"
          >
            {{ $t('action.cancel') }}
          </button>
          <button
            class="btn btn--orange"
            @click="doDelete"
          >
            {{ $t('action.confirm') }}
          </button>
        </div>
      </Modal>
    </div>
    <HKLInviteModal
      ref="inviteModal"
      :listTitle="$t('tender.representatives')"
      :accountsList="needInviteAccounts"
      @onInviteClick="() => invite(needInviteAccounts)"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapMutations, mapGetters } from 'vuex'
import { getCurrentInstance, computed, unref, ref, inject, watch } from 'vue-demi'
import InputField from '@/controls/InputField'
import Modal from '@/controls/Modal'
import HKLInviteModal from '@/controls/Modal/InviteModal'
import formFullnameMixin from '@/pages/formFullnameMixin'
import dateUtil from '@/utils/dateUtil'
import mutationTypes from '@/store/mutation-types'
import notification from '@/utils/notification'
import * as regex from '@/utils/regex'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { getLink, getVendorHomeLink } from '@/api/postman/util'
import { getTenderAccounts, updateTenderAccounts, inviteRepresentative } from '@/api/tenders/request'
import { tenderStatus } from '@/utils/locale'
import { utils as formUtils } from '@/plugin/InputChange'
import { useRefModal } from '@/controls/Modal/modal.hook'
import { useStoreGetters } from '@/store/index'
import { TENDER_EDITING_PROVIDE } from '../../../pages/tenders/consts'

export default {
  name: 'TenderRepresentative',
  components: {
    HKLInviteModal,
    InputField,
    Modal,
  },
  mixins: [formFullnameMixin],
  setup (_, { emit }) {
    const isTenderEditing = inject(TENDER_EDITING_PROVIDE)
    const { proxy } = getCurrentInstance()
    const { currentTender, accounts } = useStoreGetters('tenders', ['currentTender', 'accounts'])
    // #region InviteAllModal
    const { open, close: closeInviteAllModal, modal } = useRefModal()
    const tenderId = computed(() => {
      return lodash.get(unref(currentTender), 'general.id')
    })
    const needInviteAccounts = computed(() => {
      return unref(accounts).filter((account) => !account.tenderAccount.invitedAt)
    })
    const canEditRepresentative = computed(() => {
      const invalidStatus = [tenderStatus.AWARDED, tenderStatus.REGRETTED]
      const currentStatus = lodash.get(unref(currentTender), 'general.status')
      const isInvalidStatus = invalidStatus.includes(currentStatus)
      const hadDescription = lodash.get(unref(currentTender), 'general.projectDescription')
      return hadDescription && !isInvalidStatus && !unref(isTenderEditing)
    })
    const showAddBtn = computed(() => {
      return editable.value
    })
    const duplicatePhoneIndexMap = ref(new Map())
    const invalidHklUsername = ref([])
    const duplicateEmailIndexMap = ref(new Map())
    const editable = ref(false)

    const phoneErrClass = (index) => {
      return {'error': duplicatePhoneIndexMap.value.get(index)}
    }
    const emailErrClass = (index) => {
      const account = unref(accounts)[index]
      return {'error': duplicateEmailIndexMap.value.get(index) || (account && invalidHklUsername.value.includes(account.username))}
    }
    const invite = async (users) => {
      // change document.activeElement
      document.body.focus()
      const tid = unref(tenderId)
      await Promise.all(
        lodash.castArray(users).map(async (user) => {
          await inviteRepresentative(tid, {
            user,
            internalLink: getLink('home'),
            linkToAccess: getVendorHomeLink(),
          })
        }))
      closeInviteAllModal()
      const updatedAccounts = await getTenderAccounts(tid)
      proxy.updateAccounts(updatedAccounts)
    }
    const save = async () => {
      let valid = proxy.validate()
      if (!valid) return
      const tid = unref(tenderId)
      invalidHklUsername.value = []
      try {
        await updateTenderAccounts(tid, unref(accounts))
      } catch (err) {
        invalidHklUsername.value = lodash.get(err, 'response.data.data.invalidHklUsername', [])
        throw err
      }
      const updatedAccounts = await getTenderAccounts(tid)
      editable.value = false
      proxy.updateAccounts(updatedAccounts)
      proxy.openInviteAllModal()
    }
    // #endregion InviteAllModal
    watch(editable, () => {
      emit('update:isEditing', unref(editable))
    })
    return {
      tenderId,
      duplicatePhoneIndexMap,
      invalidHklUsername,
      duplicateEmailIndexMap,
      editable,
      emailErrClass,
      phoneErrClass,
      inviteModal: modal,
      needInviteAccounts,
      canEditRepresentative,
      showAddBtn,
      openInviteAllModal: () => {
        if (proxy.showInviteBtn && unref(needInviteAccounts).length > 0) {
          open()
        }
      },
      save,
      invite,
    }
  },
  data () {
    return {
      regex,
      prepareInvitePerson: '',
      emptyValue: '--',
      deleteIndex: '',
    }
  },
  computed: {
    ...mapGetters('tenders', ['currentTender', 'accounts']),
    ...mapGetters('user', ['currentUser']),
    showInviteBtn () {
      return this.isPicView && this.canEditRepresentative
    },
    isPicView () {
      return this.currentUser.id === this.currentTender.general.managedBy
    },
  },
  methods: {
    ...mapMutations('tenders', {
      updateTenderPathField: mutationTypes.TENDER_UPDATE_PATH_FIELD,
    }),
    isCurrentUser (item) {
      return lodash.get(this.currentUser, 'id') === lodash.get(item, 'id')
    },
    enableEdit () {
      this.editable = true
    },
    async cancel () {
      const tenderId = lodash.get(this.currentTender, 'general.id')
      const updatedAccounts = await getTenderAccounts(tenderId)
      this.updateAccounts(updatedAccounts)
      this.editable = false
    },
    buildNewAccount () {
      return {
        tenderAccount: {
          contactFirstName: '',
          contactLastName: '',
          phone: '',
          altPhone: '',
        },
        username: '',
      }
    },
    updateAccounts (value) {
      this.updateTenderPathField({
        path: ['accounts'],
        newValue: value,
      })
    },
    updateContactAttr (path, value, index) {
      const accountsList = lodash.cloneDeep(this.accounts)
      accountsList[index]['tenderAccount'][path] = value
      this.updateAccounts(accountsList)
    },
    addAccount () {
      const accountsList = lodash.cloneDeep(this.accounts)
      const newAccount = this.buildNewAccount()
      accountsList.push(newAccount)
      this.updateAccounts(accountsList)
    },
    preDeleteAccount (index) {
      const account = this.accounts[index]
      const isInvited = lodash.get(account, 'tenderAccount.invitedAt')
      if (isInvited) {
        this.deleteIndex = index
        this.openDeleteConfirmModal()
      } else {
        this.deleteAccount(index)
      }
    },
    deleteAccount (index) {
      const accountsList = lodash.cloneDeep(this.accounts)
      accountsList.splice(index, 1)
      this.updateAccounts(accountsList)
    },
    doDelete () {
      this.deleteAccount(this.deleteIndex)
      this.closeDeleteConfirmModal()
    },
    updateContactUsername (value, index) {
      const accountsList = lodash.cloneDeep(this.accounts)
      accountsList[index]['username'] = value
      delete accountsList[index].id
      this.updateAccounts(accountsList)
    },

    openDeleteConfirmModal () {
      this.$refs['confirm-delete-representative'].open()
    },
    closeDeleteConfirmModal () {
      this.$refs['confirm-delete-representative'].close()
    },
    checkoutNotEqualDash () {
      let validate = true
      this.accounts.map(account => {
        if (!account.username || !account.tenderAccount.contactFirstName || !account.tenderAccount.contactLastName || !account.tenderAccount.phone) {
          validate = false
        }
      })
      return validate
    },
    validate () {
      formUtils.resetDirty()
      this.duplicateEmailIndexMap = new Map()
      this.duplicatePhoneIndexMap = new Map()
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'none'}, '.tender-representative')
      const isValidEmails = this.validateDuplicateEmail()
      const isValidPhones = this.validateDuplicatePhone()
      return fusionInputCheckUtil.isAllFieldsValid('.tender-representative') && this.checkoutNotEqualDash() && isValidEmails && isValidPhones
    },
    validateDuplicateEmail () {
      let validate = true
      const uniqueEmailArr = new Map()
      this.accounts.map((account, index) => {
        if (uniqueEmailArr.has(account.username) && account.username !== '') {
          this.duplicateEmailIndexMap[index - this.currentTender.accounts.length] = true
          validate = false
          notification.fireNotification(false, this.$t('hint.inputTip.emailIsExisting'))
        } else {
          uniqueEmailArr.set(account.username, 1)
        }
      })
      return validate
    },
    validateDuplicatePhone () {
      let validate = true
      this.accounts.map((account, index) => {
        if (account.tenderAccount.phone === account.tenderAccount.altPhone) {
          validate = false
          if (account.tenderAccount.phone) {
            this.duplicatePhoneIndexMap[index] = true
            notification.fireNotification(false, this.$t('hint.inputTip.phoneShouldBeDifferent'))
          }
        }
      })
      return validate
    },
    displayFullName (contactFirstName = '', contactLastName = '') {
      return this.displayI18nFullName(contactFirstName, contactLastName) || this.$t('emptyValue')
    },
    formatDate (time) {
      return time ? dateUtil.format(time, 'DD MMM YY HH:mm') : '-'
    },

    openModal () {
      this.$refs['confirm-duplicate-representative'].open()
    },
    closeModal () {
      this.$refs['confirm-duplicate-representative'].close()
    },
  },
}
</script>

<style lang="scss">
.tender-representative {
  max-width: 1440px;
  margin: auto;
  padding: 20px 24px;
  .tender-representative-content {
    background: #fafafa;
    padding: 20px 24px;

    .tenancy-accounts {
      margin-bottom: 20px;
    }
    .contractor-info-head {
      padding-bottom: 15px;
    }

    .editabel {
      margin-left: 15px;
    }
    .top-title {
      font-size: 16px;
      font-weight: bold;
      float: left;
    }
    .contractor-info-buttons {
      float: right;
    }
    .content {
      display: flex;
      position: relative;
      .error-highlight {
        background: red;
      }
      .input-field label {
        margin: 0 0 5px 0;
      }
      i.fa-times {
        cursor: pointer;
        position: absolute;
        right: -25px;
        bottom: 15px;
      }
      i.fa-times:before {
        font-size: 20px;
      }
      .name,
      .email,
      .phone,
      .alt-phone,
      .phone-tenancy,
      .alt-phone-tenancy {
        display: inline-block;
        flex: 1;
        input {
          width: 100%;
        }
        margin-right: 30px;
      }
      .error input {
        background-color: #f9ecea !important;
      }
      .name {
        min-width: 230px;
        font-size: 0;
        .input-field {
          font-size: 14px;
          width: 50%;
          display: inline-block;
          .text-input {
            margin: 0;
          }
        }
        .input-field:first-of-type {
          vertical-align: bottom;
        }
      }
      .alt-phone,
      .alt-phone-tenancy {
        margin: 0;
      }
    }
    .addAccount {
      margin-top: 10px;
      text-decoration: underline;
      font-style: italic;
      cursor: pointer;
    }
    .display-table.creator {
      li {
        height: 60px;
        line-height: 60px;
        span {
          line-height: 1.43;
        }
      }
    }
    .display-table {
      $serial-width: 50px;
      list-style-type: none;
      margin: 0 0 0 15px;
      .table-header {
        font-size: 12px;
        height: 30px;
        color: gray;
        .name {
          font-weight: normal;
        }
      }
      .table-header,
      .table-content {
        display: flex;
        align-items: center;
        height: 60px;
      }
      span {
        display: inline-block;
      }
      .serial {
        font-size: 12px;
        width: $serial-width;
        min-width: $serial-width;
        max-width: $serial-width;
      }
      .name {
        padding-right: 20px;
        flex: 1.4;
        word-break: break-word;
        word-wrap: break-word;
      }
      .email {
        flex: 1.8;
        padding-right: 20px;
        word-break: break-word;
        word-wrap: break-word;
      }
      .error input {
        background-color: #f9ecea !important;
      }
      .phone,
      .other-phone,
      .lastlogin,
      .invited-at {
        padding-right: 20px;
        flex: 1;
        word-break: break-word;
        word-wrap: break-word;
      }
      .invite {
        line-height: 60px;
        width: 80px;
        text-align: center;
      }
      .invite-btn {
        display: inline-block;
        text-transform: capitalize;
      }

      li {
        font-size: 14px;
      }
      li:nth-child(even) {
        background: #fafbfc;
      }
      .name {
        font-weight: bold;
      }
    }
  }
}
</style>
